import { useState, useEffect } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useApp } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { motion } from "framer-motion";
import api from "@/lib/api";

export default function AuthPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [step, setStep] = useState("email");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [forgotSent, setForgotSent] = useState(false);
  const { login, user, t } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) {
          clearInterval(id);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const handleCheckEmail = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;

    setLoading(true);
    try {
      const res = await api.post("/auth/check-email", { email: trimmed });
      if (res.data.exists) {
        setStep("password");
      } else {
        navigate("/register", { state: { email: trimmed, from } });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t("Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success(t("Welcome back!"));
      navigate(from, { replace: true });
    } catch (e) {
      if (e.response?.status === 429) {
        toast.error(t("Too many attempts. Please wait a minute before trying again."));
        setCooldown(60);
      } else {
        toast.error(e.response?.data?.detail || t("Invalid credentials"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep("email");
    setPassword("");
  };

  const handleForgotPassword = async () => {
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setForgotSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  if (user) {
    return <Navigate to="/profile" replace />;
  }

  return (
    <div className="p-6 max-w-md mx-auto" data-testid="auth-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="font-playfair text-3xl font-bold text-[#EDEDED] mb-2">
          {t("Welcome back")}
        </h1>
        <p className="text-[#D4D4D8] mb-6 text-sm">
          {t("Sign in to continue")}
        </p>

        {step === "email" ? (
          <form onSubmit={handleCheckEmail} className="space-y-4">
            <div>
              <Label className="text-[#D4D4D8] text-xs mb-1">{t("Email")}</Label>
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@email.com" className="bg-[#121212] border-[#27272A] text-[#EDEDED] h-11" data-testid="input-email" required autoFocus />
            </div>
            <Button type="submit" disabled={loading || cooldown > 0 || !email.trim()} className="w-full bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] font-semibold h-12 disabled:opacity-50" data-testid="auth-continue-btn">
              {cooldown > 0 ? `${t("Wait")} ${cooldown}s` : loading ? t("Please wait...") : t("Continue")}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="bg-[#121212] rounded-xl p-4 border border-[#27272A]">
              <p className="text-[#D4D4D8] text-xs mb-1">{t("Email")}</p>
              <div className="flex items-center justify-between">
                <span className="text-[#EDEDED] font-medium text-sm truncate mr-2">{email}</span>
                <button type="button" onClick={handleBack} className="text-[#D4B483] hover:text-[#E5CFA5] text-sm font-medium hover:underline whitespace-nowrap transition-colors">
                  {t("Change email")}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-[#D4D4D8] text-xs mb-1">{t("Password")}</Label>
              <Input type="password" value={password} onChange={e => setPassword(e.target.value)}
                placeholder="Min 6 characters" className="bg-[#121212] border-[#27272A] text-[#EDEDED] h-11" data-testid="input-password" required autoFocus />
            </div>
            <div className="flex justify-end -mt-1">
              <button type="button" onClick={handleForgotPassword} className="text-[#D4B483] hover:text-[#E5CFA5] text-xs font-medium hover:underline transition-colors">
                {t("Forgot password?")}
              </button>
            </div>
            <Button type="submit" disabled={loading || cooldown > 0 || !password} className="w-full bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] font-semibold h-12 disabled:opacity-50" data-testid="auth-submit-btn">
              {cooldown > 0 ? `${t("Wait")} ${cooldown}s` : loading ? t("Please wait...") : t("Sign In")}
            </Button>
          </form>
        )}
        {forgotSent && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-[#121212] rounded-xl p-5 border border-[#27272A] text-center mt-6">
            <div className="w-12 h-12 rounded-full bg-[#D4B483]/20 flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-[#D4B483]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
            </div>
            <h2 className="text-[#EDEDED] font-semibold mb-1">{t("Check your email")}</h2>
            <p className="text-[#D4D4D8] text-sm">
              {t("If the email exists, a reset link has been sent.")}
            </p>
            <Button variant="outline" onClick={() => { setForgotSent(false); setStep("email"); }} className="mt-4 border-[#27272A] text-[#D4D4D8] hover:text-[#EDEDED]">
              {t("Back to Sign In")}
            </Button>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}
